import { ACTION_DEFINITIONS, ACTIONS_ORDER, PRIMARY_ACTIONS, getLoadingFlags } from './actionsConfig';

function requestRequiresAcceptance(request) {
    const workflowConfig = request?.RequestType?.workflow_config || {};
    return workflowConfig.requiresAcceptance !== false;
}

function getActionDefinitionForRequest(definition, request) {
    if (!definition || definition.key !== 'complete') {
        return definition;
    }

    if (request?.is_multi_executor === true) {
        return {
            ...definition,
            text: 'Завершить свою часть',
            loadingText: 'Завершение своей части...'
        };
    }

    if (requestRequiresAcceptance(request)) {
        return definition;
    }

    return {
        ...definition,
        text: 'Завершить заявку',
        loadingText: 'Выполнение...'
    };
}

function getCustomFieldValues(request) {
    const workflowData = request && request.workflow_data ? request.workflow_data : {};
    const values = workflowData.custom_fields || workflowData.customFields || {};
    return values && typeof values === 'object' && !Array.isArray(values) ? values : {};
}

function isSameJsonValue(left, right) {
    return JSON.stringify(left || {}) === JSON.stringify(right || {});
}

/**
 * Геттеры для карточки заявки
 * 
 * Права доступа читаются из state.request.permissions — единой точки истины на бэкенде.
 * UI-логика (форматирование, фильтрация данных) остаётся здесь.
 */
export default {
    // ========================================
    // Права доступа (из бэкенда permissions)
    // ========================================

    /**
     * Может ли пользователь редактировать детали заявки
     */
    canEditRequestDetails: (state) => {
        return state.request?.permissions?.canEditRequestDetails ?? false;
    },

    canEditRequestObject: (state) => {
        return state.request?.permissions?.canEditRequestObject === true;
    },

    /**
     * Может ли пользователь менять срок выполнения и срочность заявки
     */
    canEditRequestDeadline: (state) => {
        return state.request?.permissions?.canEditRequestDeadline ?? false;
    },

    /**
     * Может ли пользователь редактировать позиции заявки
     */
    canEditItems: (state) => {
        return state.request?.permissions?.canEditItems ?? false;
    },

    /**
     * Может ли пользователь редактировать список материалов
     */
    canEditMaterialsList: (state) => {
        return state.request?.permissions?.canEditMaterialsList ?? false;
    },

    /**
     * Может ли пользователь отгружать материалы
     */
    canShipMaterials: (state) => {
        return state.request?.permissions?.canShipMaterials ?? false;
    },

    /**
     * Может ли пользователь принимать материалы
     */
    canAcceptMaterials: (state) => {
        return state.request?.permissions?.canAcceptMaterials ?? false;
    },

    /**
     * Может ли текущий пользователь назначать исполнителя
     */
    canAssignExecutor: (state) => {
        return state.request?.permissions?.canAssign ?? false;
    },

    /**
     * Нужно ли показывать интерфейс выбора исполнителя
     */
    shouldShowExecutorSelector: (state) => {
        return state.request?.permissions?.showExecutorSelector ?? false;
    },

    /**
     * Является ли это переназначением исполнителя
     */
    isReassignment: (state) => {
        return state.request?.permissions?.isReassignment ?? false;
    },

    /**
     * Включён ли режим «несколько исполнителей по строкам материалов»
     */
    isMultiExecutor: (state) => {
        return !!state.request?.is_multi_executor;
    },

    /**
     * Может ли пользователь включить режим multi (UI-чекбокс)
     */
    canEnableMultiExecutor: (state) => {
        return state.request?.permissions?.canEnableMultiExecutor ?? false;
    },

    /**
     * Может ли пользователь переназначать исполнителя одной строки
     */
    canReassignItemExecutor: (state) => {
        return state.request?.permissions?.canReassignItemExecutor ?? false;
    },

    canEnableSelfAssignMode: (state) => {
        return state.request?.permissions?.canEnableSelfAssignMode ?? false;
    },

    canSelfAssignItemExecutor: (state) => {
        return state.request?.permissions?.canSelfAssignItemExecutor ?? false;
    },

    canSelfReleaseItemExecutor: (state) => {
        return state.request?.permissions?.canSelfReleaseItemExecutor ?? false;
    },

    canSelfAssignBulk: (state) => {
        return state.request?.permissions?.canSelfAssignBulk ?? false;
    },

    canManageFuelCardSelection: (state) => {
        return state.request?.permissions?.canManageFuelCardSelection ?? false;
    },

    canManageFuelCardLimits: (state) => {
        return state.request?.permissions?.canManageFuelCardLimits ?? false;
    },

    canManageMechanicAssetSelection: (state) => {
        return state.request?.permissions?.canManageMechanicAssetSelection ?? false;
    },

    /**
     * Все ли строки в multi-режиме имеют исполнителя
     * Используется для блокировки кнопки сохранения
     */
    multiExecutorAssignmentsValid: (state, getters) => {
        if (!getters.isMultiExecutor) {
            return true;
        }
        return getters.materialItems.every(i => !!(i.executor && i.executor.user_id) || !!i.executor_id);
    },

    /**
     * Может ли пользователь назначить исполнителя (с учетом UI-состояния)
     */
    canAssign: (state, getters) => {
        return getters.canAssignExecutor && getters.shouldShowExecutorSelector;
    },

    // ========================================
    // Проверка доступности действий (из permissions)
    // ========================================

    canSubmit: (state) => {
        return state.request?.permissions?.canSubmit ?? false;
    },

    canSave: (state, getters) => {
        return getters.canEditRequestDetails;
    },

    canApprove: (state) => {
        return state.request?.permissions?.canApprove ?? false;
    },

    canReject: (state) => {
        return state.request?.permissions?.canReject ?? false;
    },

    canExecute: (state) => {
        return state.request?.permissions?.canExecute ?? false;
    },

    canReturn: (state) => {
        return state.request?.permissions?.canReturn ?? false;
    },

    canComplete: (state) => {
        return state.request?.permissions?.canComplete ?? false;
    },

    canPartiallyComplete: (state) => {
        return state.request?.permissions?.canPartiallyComplete ?? false;
    },

    canAccept: (state) => {
        return state.request?.permissions?.canAccept ?? false;
    },

    canAcceptPartially: (state) => {
        return state.request?.permissions?.canAcceptPartially ?? false;
    },

    canCancel: (state) => {
        return state.request?.permissions?.canCancel ?? false;
    },

    canCopy: (state) => {
        return state.request?.permissions?.canCopy ?? false;
    },

    canChangeStatus: (state) => {
        return state.request?.permissions?.canChangeStatus ?? false;
    },

    canReplaceApprover: (state) => {
        return state.request?.permissions?.canReplaceApprover ?? false;
    },

    canReplaceApproverWithAnyEmployee: (state) => {
        return state.request?.permissions?.canReplaceApproverWithAnyEmployee ?? false;
    },

    canRemoveApprover: (state) => {
        return state.request?.permissions?.canRemoveApprover ?? false;
    },

    /**
     * Является ли пользователь админом
     */
    isCurrentUserAdmin: (state) => {
        return state.request?.isAdmin === true;
    },

    // ========================================
    // UI-логика (форматирование данных)
    // ========================================

    /**
     * Комментарии из активности
     */
    activityComments: (state) => {
        const items = (state.requestActivity || []).filter(i => i.type === 'comment');
        return items.map(i => ({
            id: i.id,
            user_id: i.user_id,
            comment_text: i.text,
            is_system_comment: !!i.is_system_comment,
            created_at: i.created_at,
            Author: i.Author || null,
            Files: i.Files || []
        }));
    },

    /**
     * История из активности
     */
    activityHistory: (state) => {
        const items = (state.requestActivity || []).filter(i => i.type === 'log');
        return items.map(i => ({
            id: i.id,
            action: i.action || 'log',
            description: i.description || i.text || '',
            executor_name: i.executor_name,
            user_id: i.user_id,
            created_at: i.created_at,
            User: i.User || null
        }));
    },

    /**
     * Инициатор заявки
     */
    initiator: (state) => {
        if (!state.request) return null;
        if (state.request.Participants?.length) {
            const initiatorParticipant = state.request.Participants.find(p => p.role === 'initiator');
            if (initiatorParticipant?.User) {
                return initiatorParticipant;
            }
        }
        if (state.request.Initiator) {
            return { User: state.request.Initiator };
        }
        return null;
    },

    /**
     * Согласующие
     */
    approvers: (state) => {
        if (!state.request?.Participants) return [];
        return state.request.Participants.filter(p => p.role === 'approver');
    },

    /**
     * Исполнители
     */
    executors: (state) => {
        if (!state.request?.Participants) return [];
        return state.request.Participants.filter(p => p.role === 'executor');
    },

    /**
     * Назначающие исполнителя
     */
    assigners: (state) => {
        if (!state.request?.Participants) return [];
        return state.request.Participants.filter(p => p.role === 'assigner');
    },

    /**
     * Наблюдатели
     */
    observers: (state) => {
        if (!state.request?.Participants) return [];
        return state.request.Participants.filter(p => p.role === 'observer');
    },

    /**
     * Опции для селектора исполнителей
     * Ручное назначение: маппинг API GetAvailableExecutors (активный уже исключён на бэке)
     * Автоматическое: маппинг executors из Participants
     */
    executorOptions: (state, getters) => {
        const toOption = (e) => {
            const name = `${e.first_name ?? e.User?.first_name ?? ''} ${e.last_name ?? e.User?.last_name ?? ''}`.trim();
            return { value: e.user_id, text: name || `Пользователь #${e.user_id}`, priority: e.priority };
        };

        if (state.request?.RequestType?.executor_assignment_manual) {
            return (state.availableExecutors || []).map(toOption);
        }

        const executors = getters.executors || [];
        const list = getters.isReassignment ? executors.filter(e => e.status !== 'active') : executors;
        return list.map(toOption);
    },

    /**
     * Подсказки для участников (из бэкенда)
     */
    userActionHints: (state) => {
        return state.request?.actionHints || [];
    },

    // ========================================
    // Материалы и позиции
    // ========================================

    /**
     * Проверка наличия материалов в заявке
     */
    hasMaterials: (state) => {
        if (!state.request?.items || !Array.isArray(state.request.items)) {
            return false;
        }
        return state.request.items.some(item => item.item_type === 'material');
    },

    /**
     * Список материалов
     */
    materialItems: (state) => {
        if (!state.request?.items || !Array.isArray(state.request.items)) {
            return [];
        }
        return state.request.items.filter(item => item.item_type === 'material');
    },

    /**
     * Материалы, доступные текущему пользователю для отгрузки.
     * В одиночном режиме — все, в multi-режиме — только свои (canShip=true).
     */
    materialItemsForCurrentUser: (state, getters) => {
        return getters.materialItems.filter(i => i.canShip !== false);
    },

    /**
     * Список не-материалов
     */
    nonMaterialItems: (state) => {
        if (!state.request?.items || !Array.isArray(state.request.items)) {
            return [];
        }
        return state.request.items.filter(item => item.item_type !== 'material');
    },

    // ========================================
    // Отслеживание изменений формы
    // ========================================

    /**
     * Есть ли изменения в позициях (не-материалах)
     */
    itemsHaveChanges: (state) => {
        if (!state.request || !state.originalRequest) return false;
        const currentItems = Array.isArray(state.request.items)
            ? state.request.items.filter(item => item.item_type !== 'material')
            : [];
        const originalItems = Array.isArray(state.originalRequest.items)
            ? state.originalRequest.items.filter(item => item.item_type !== 'material')
            : [];
        if (currentItems.length !== originalItems.length) {
            return true;
        }
        const originalMap = new Map(originalItems.map(item => [item.id, item]));
        return currentItems.some(item => {
            const original = originalMap.get(item.id);
            if (!original) return true;
            return ['item_name', 'quantity', 'unit'].some(key => original[key] !== item[key]);
        });
    },

    /**
     * Есть ли изменения в файлах
     */
    filesHaveChanges: (state) => {
        if (!state.request || !state.originalRequest) return false;
        if (state.newAttachments.length || state.removedFileIds.length) {
            return true;
        }
        const currentFiles = Array.isArray(state.request.files) ? state.request.files : [];
        const originalFiles = Array.isArray(state.originalRequest.files) ? state.originalRequest.files : [];
        if (currentFiles.length !== originalFiles.length) {
            return true;
        }
        const currentIds = currentFiles.map(file => file.id).sort();
        const originalIds = originalFiles.map(file => file.id).sort();
        return currentIds.some((id, idx) => id !== originalIds[idx]);
    },

    /**
     * Есть ли изменения в списке материалов
     */
    materialsListHaveChanges: (state) => {
        return state.materialsChanges.added.length > 0 ||
            state.materialsChanges.updated.length > 0 ||
            state.materialsChanges.deleted.length > 0;
    },

    /**
     * Есть ли изменения в форме
     */
    hasFormChanges: (state, getters) => {
        if (!state.request || !state.originalRequest) return false;
        
        const allowedFields = [
            'request_type_id',
            'title',
            'description',
            'priority',
            'deadline',
            'urgency_comment',
            'building_process_id'
        ];

        const hasFieldChanges = allowedFields.some(field => {
            if (!Object.prototype.hasOwnProperty.call(state.request, field)) {
                return false;
            }
            const currentValue = state.request[field];
            const originalValue = state.originalRequest ? state.originalRequest[field] : undefined;
            return currentValue !== originalValue;
        });

        if (hasFieldChanges) return true;
        if (!isSameJsonValue(
            getCustomFieldValues(state.request),
            getCustomFieldValues(state.originalRequest)
        )) return true;
        if (getters.itemsHaveChanges) return true;
        if (getters.filesHaveChanges) return true;
        if (getters.materialsListHaveChanges) return true;
        return false;
    },

    pendingChangesSummary: (state, getters) => {
        if (!state.request || !state.originalRequest) return [];
        const labels = {building_process_id: 'Объект', description: 'Описание', title: 'Название',
            request_type_id: 'Тип заявки', priority: 'Приоритет', deadline: 'Срок', urgency_comment: 'Причина срочности'};
        const result = Object.keys(labels).filter(key => Object.prototype.hasOwnProperty.call(state.request, key) &&
            state.request[key] !== state.originalRequest[key]).map(key => labels[key]);
        if (!isSameJsonValue(getCustomFieldValues(state.request), getCustomFieldValues(state.originalRequest))) result.push('Дополнительные поля');
        if (getters.filesHaveChanges) result.push('Вложения');
        if (getters.itemsHaveChanges || getters.materialsListHaveChanges) result.push('Позиции');
        return result;
    },

    // ========================================
    // Кнопки действий
    // ========================================

    /**
     * Проверка, идет ли какая-либо загрузка
     */
    isAnyActionLoading: (state) => {
        const loadingFlags = getLoadingFlags();
        return state.uploadingRequiredAttachment || loadingFlags.some(flag => state[flag]);
    },

    /**
     * Получить доступные кнопки действий
     */
    actionButtons: (state, getters) => {
        if (!state.request?.id) {
            return [];
        }

        const anyLoading = getters.isAnyActionLoading;

        return ACTIONS_ORDER
            .map(key => ACTION_DEFINITIONS[key])
            .filter(def => {
                return getters[def.conditionGetter];
            })
            .filter(def => {
                if (def.key !== 'complete') {
                    return true;
                }
                return !(state.request.status === 'awaiting_acceptance' && getters.canAccept);
            })
            .map(def => {
                const actionDef = getActionDefinitionForRequest(def, state.request);
                const isLoading = actionDef.loadingFlag ? state[actionDef.loadingFlag] : false;
                
                let disabled = anyLoading;
                if (actionDef.requiresChanges) {
                    disabled = !getters.hasFormChanges || anyLoading;
                }

                return {
                    key: actionDef.key,
                    icon: actionDef.icon,
                    text: actionDef.text,
                    variant: actionDef.variant,
                    loadingText: actionDef.loadingText,
                    disabled,
                    loading: isLoading,
                    isPrimary: actionDef.isPrimary || false
                };
            });
    },

    /**
     * Первичные кнопки действий
     */
    primaryActionButtons: (state, getters) => {
        return getters.actionButtons.filter(action => PRIMARY_ACTIONS.includes(action.key));
    },

    /**
     * Вторичные кнопки действий
     */
    secondaryActionButtons: (state, getters) => {
        return getters.actionButtons.filter(action => !PRIMARY_ACTIONS.includes(action.key));
    }
};
